import {
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  copyCourseContent,
  previewCourseCopy
} from '../../../utils/courseCopy';

function courseName(course) {
  return (
    course
      ?.course_titles
      ?.name ||
    course?.name ||
    'Untitled Course'
  );
}

function fullCourseName(course) {
  return (
    courseName(course) +
    ' — ' +
    (
      course
        ?.semesters
        ?.name ||
      'Semester not specified'
    )
  );
}

export default function CourseImportModal({
  course,
  courses,
  onClose,
  onImported
}) {
  const [
    sourceCourseId,
    setSourceCourseId
  ] = useState('');

  const [
    preview,
    setPreview
  ] = useState(null);

  const [
    previewLoading,
    setPreviewLoading
  ] = useState(false);

  const [
    saving,
    setSaving
  ] = useState(false);

  const [
    error,
    setError
  ] = useState('');

  const sourceOptions =
    useMemo(
      () =>
        (Array.isArray(courses)
          ? courses
          : [])
          .filter(
            candidate =>
              String(
                candidate.id
              ) !==
              String(
                course.id
              )
          ),
      [
        courses,
        course.id
      ]
    );

  useEffect(() => {
    let cancelled = false;

    setPreview(null);
    setError('');

    if (!sourceCourseId) {
      return undefined;
    }

    setPreviewLoading(true);

    previewCourseCopy({
      sourceCourseId,
      targetCourseId:
        course.id
    })
      .then(result => {
        if (
          !cancelled
        ) {
          setPreview(result);
        }
      })
      .catch(err => {
        if (
          !cancelled
        ) {
          setError(
            err?.message ||
            'Could not preview this import.'
          );
        }
      })
      .finally(() => {
        if (
          !cancelled
        ) {
          setPreviewLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    sourceCourseId,
    course.id
  ]);

  async function handleImport() {
    if (
      saving ||
      !sourceCourseId
    ) {
      return;
    }

    // COURSE_IMPORT_CONFIRMATION_20260920
    const sourceCourse =
      sourceOptions.find(
        candidate =>
          String(candidate.id) ===
          String(sourceCourseId)
      );

    if (!sourceCourse) {
      setError(
        'The selected source course is no longer available.'
      );
      return;
    }

    const confirmed =
      window.confirm(
        `Import ${fullCourseName(sourceCourse)} into ${fullCourseName(course)}?\n\n` +
        'This will copy the source course configuration and content into the selected empty course shell.'
      );

    if (!confirmed) {
      return;
    }

    setSaving(true);
    setError('');

    try {
      await copyCourseContent({
        sourceCourseId,
        targetCourseId:
          course.id
      });

      await onImported();
    } catch (err) {
      setError(
        err?.message ||
        'The course could not be imported.'
      );
    } finally {
      setSaving(false);
    }
  }

  const blockers =
    preview?.blockers || [];

  const canImport =
    !!sourceCourseId &&
    !!preview &&
    !previewLoading &&
    !saving &&
    blockers.length === 0;

  return (
    <div
      style={styles.overlay}
      onClick={
        saving
          ? undefined
          : onClose
      }
    >
      <div
        style={styles.modal}
        onClick={event =>
          event.stopPropagation()
        }
      >
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>
              Import Course
            </h2>

            <div style={styles.subtitle}>
              Into{' '}
              <strong>
                {fullCourseName(
                  course
                )}
              </strong>
            </div>
          </div>

          <button
            type="button"
            style={styles.close}
            disabled={saving}
            onClick={onClose}
          >
            ×
          </button>
        </div>

        {error && (
          <div style={styles.error}>
            {error}
          </div>
        )}

        <div style={styles.field}>
          <label style={styles.label}>
            Import from
          </label>

          <select
            style={styles.input}
            value={sourceCourseId}
            disabled={saving}
            onChange={event =>
              setSourceCourseId(
                event.target.value
              )
            }
          >
            <option value="">
              Select a course
            </option>

            {sourceOptions.map(
              source => (
                <option
                  key={source.id}
                  value={source.id}
                >
                  {fullCourseName(
                    source
                  )}
                </option>
              )
            )}
          </select>
        </div>

        {previewLoading && (
          <div style={styles.note}>
            Checking source and target…
          </div>
        )}

        {preview && (
          <>
            <div style={styles.summary}>
              <div>
                <strong>
                  {
                    preview
                      .summary
                      .assignments
                  }
                </strong>
                <span>
                  Assignments
                </span>
              </div>

              <div>
                <strong>
                  {
                    preview
                      .summary
                      .readings
                  }
                </strong>
                <span>
                  Readings
                </span>
              </div>

              <div>
                <strong>
                  {
                    preview
                      .summary
                      .events
                  }
                </strong>
                <span>
                  Events
                </span>
              </div>

              <div>
                <strong>
                  {
                    preview
                      .summary
                      .syllabusSections
                  }
                </strong>
                <span>
                  Syllabus sections
                </span>
              </div>
            </div>

            {blockers.length > 0 ? (
              <div style={styles.blocker}>
                <strong>
                  Import cannot proceed.
                </strong>

                {blockers.map(
                  (
                    blocker,
                    index
                  ) => (
                    <div
                      key={index}
                      style={
                        styles.blockerLine
                      }
                    >
                      {blocker}
                    </div>
                  )
                )}
              </div>
            ) : (
              <div style={styles.note}>
                The target is an empty, non-live
                course shell. Import will copy
                course settings, syllabus,
                assignments, Structured Forms,
                Teaching Schedule descriptions,
                readings, Events, AI permissions,
                attendance-group structure and
                assignment scheduling. Dates are
                remapped by teaching week and
                meeting position. Students,
                submissions, grades, attendance
                records, selections and group
                memberships are never copied.
                Custom Group names are not copied,
                so imported Groups use the normal
                Group 1, Group 2, … names.
              </div>
            )}
          </>
        )}

        <div style={styles.footer}>
          <button
            type="button"
            style={styles.cancelButton}
            disabled={saving}
            onClick={onClose}
          >
            Cancel
          </button>

          <button
            type="button"
            style={styles.importButton}
            disabled={!canImport}
            onClick={handleImport}
          >
            {saving
              ? 'Importing…'
              : 'Import Course'}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 520,
    background:
      'rgba(0,0,0,0.48)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px'
  },

  modal: {
    width: '100%',
    maxWidth: '680px',
    maxHeight: '90vh',
    overflowY: 'auto',
    background: 'white',
    borderRadius: '12px',
    padding: '22px',
    boxShadow:
      '0 20px 60px rgba(0,0,0,0.22)'
  },

  header: {
    display: 'flex',
    justifyContent:
      'space-between',
    gap: '16px',
    marginBottom: '18px'
  },

  title: {
    margin: 0,
    color: '#1f2933',
    fontSize: '21px'
  },

  subtitle: {
    marginTop: '5px',
    color: '#66788a',
    fontSize: '13px'
  },

  close: {
    border: 'none',
    background: 'transparent',
    color: '#66788a',
    fontSize: '26px',
    lineHeight: 1,
    cursor: 'pointer'
  },

  field: {
    marginBottom: '16px'
  },

  label: {
    display: 'block',
    marginBottom: '6px',
    color: '#1f2933',
    fontSize: '13px',
    fontWeight: '700'
  },

  input: {
    width: '100%',
    boxSizing: 'border-box',
    border:
      '1px solid #d7dee5',
    borderRadius: '8px',
    padding: '9px 11px',
    background: 'white',
    fontSize: '14px'
  },

  summary: {
    display: 'grid',
    gridTemplateColumns:
      'repeat(4, minmax(0, 1fr))',
    gap: '8px',
    marginBottom: '14px'
  },

  note: {
    padding: '11px 13px',
    border:
      '1px solid #d7dee5',
    borderRadius: '8px',
    background: '#f7f9fa',
    color: '#52616b',
    fontSize: '13px',
    lineHeight: 1.5
  },

  blocker: {
    padding: '11px 13px',
    border:
      '1px solid #e4b9b9',
    borderRadius: '8px',
    background: '#fff4f4',
    color: '#8f2929',
    fontSize: '13px',
    lineHeight: 1.45
  },

  blockerLine: {
    marginTop: '5px'
  },

  error: {
    marginBottom: '14px',
    padding: '10px 12px',
    border:
      '1px solid #e8bcbc',
    borderRadius: '8px',
    background: '#faeaea',
    color: '#a12a2a',
    fontSize: '13px'
  },

  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginTop: '20px'
  },

  cancelButton: {
    border:
      '1px solid #b8c6d1',
    borderRadius: '8px',
    padding: '9px 14px',
    background: 'white',
    color: '#334e68',
    fontWeight: '700',
    cursor: 'pointer'
  },

  importButton: {
    border: 'none',
    borderRadius: '8px',
    padding: '9px 14px',
    background: '#215b7d',
    color: 'white',
    fontWeight: '700',
    cursor: 'pointer'
  }
};
